import { UNUSED_PARAMETER } from 'hooks/metrics/shared/constants';
import { normalizeText } from './normalizeText';

const MATCH_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

export const ACTIONABLE_MATCH_STATUSES = [MATCH_STATUS.IN_PROGRESS, MATCH_STATUS.COMPLETED];

const STATUS_CARD_BY_STATUS = {
  [MATCH_STATUS.IN_PROGRESS]: 'minuto a minuto',
  [MATCH_STATUS.COMPLETED]: 'finalizado',
};

const formatStartDate = (rawDate) => {
  if (!rawDate) return UNUSED_PARAMETER;
  const d = new Date(rawDate);
  if (Number.isNaN(d.getTime())) return UNUSED_PARAMETER;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd}`;
};

const formatMatchTime = (rawDate, displayTime) => {
  if (displayTime) return displayTime;
  if (!rawDate) return UNUSED_PARAMETER;
  const d = new Date(rawDate);
  if (Number.isNaN(d.getTime())) return UNUSED_PARAMETER;
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${min}`;
};

/**
 * Devuelve el override de campos para un partido, segun su status.
 *
 * - Si match es null/undefined o status no es IN_PROGRESS ni COMPLETED -> retorna null.
 *   El consumidor debe interpretar null como "no disparar evento".
 *
 * - Si IN_PROGRESS: location, start_date, match_time con valor real;
 *   final_score y match_winner como "no aplica"; status_card "minuto a minuto".
 *
 * - Si COMPLETED: los 5 con valor real; status_card "finalizado".
 *
 * Forma esperada del objeto match (carousel):
 *   { status, scheduledAt, displayTime?, venue: { name, city },
 *     score: { home, away }, homeTeam: { name }, awayTeam: { name } }
 */
export const buildMatchValues = (match) => {
  if (!match) return null;

  const status = match.status;
  if (status !== MATCH_STATUS.IN_PROGRESS && status !== MATCH_STATUS.COMPLETED) {
    return null;
  }

  const isCompleted = status === MATCH_STATUS.COMPLETED;

  const venue = match.venue;
  const location = venue ? [venue.name, venue.city].filter(Boolean).join(', ') : null;

  const homeScore = match.score?.home;
  const awayScore = match.score?.away;
  const finalScore = (homeScore != null && awayScore != null)
    ? `${homeScore}-${awayScore}`
    : UNUSED_PARAMETER;

  const winner = (() => {
    if (homeScore == null || awayScore == null) return null;
    if (Number(homeScore) > Number(awayScore)) return match.homeTeam?.name;
    if (Number(awayScore) > Number(homeScore)) return match.awayTeam?.name;
    return 'empate';
  })();

  const homeName = normalizeText(match.homeTeam?.name);
  const awayName = normalizeText(match.awayTeam?.name);
  const contentName = homeName && awayName ? `${homeName} vs ${awayName}` : UNUSED_PARAMETER;

  return {
    content_name: contentName,
    content_location: location ? normalizeText(location) : UNUSED_PARAMETER,
    content_start_date: formatStartDate(match.scheduledAt),
    match_time: formatMatchTime(match.scheduledAt, match.displayTime),
    final_score: isCompleted ? finalScore : UNUSED_PARAMETER,
    match_winner: isCompleted && winner ? normalizeText(winner) : UNUSED_PARAMETER,
    status_card: STATUS_CARD_BY_STATUS[status],
  };
};
